// @ts-check
import { computed, ref, readonly } from 'vue';
import { useRoute, useRouter } from 'vue-router/composables';
import { useActions, useGetters } from 'vuex-composition-helpers/dist';

import useBulkSendRoutes from '@/composables/bulkSend/routes';

/**
 * How long to delay setting the isBusyOnNext or
 * isBusyOnSave values, in milliseconds
 */
const BUSY_DELAY = 1000;

/**
 * BulkSends creation flow/steps route stuff
 *
 * @param {string} [action]
 * @param {import('vue').ComputedRef} [update]
 * @param {import('vue').ComputedRef<boolean>} [valid]
 * @param {(error:any) => void} [onError]
 *
 */
export function useBulkSendStepsNavigation(action, update, valid, onError) {
  const validOrTrue = computed(() => (valid ? valid.value : true));
  const router = useRouter();
  const currentRoute = useRoute();

  const {
    progressUpdate,
    prevRoute,
    nextRoute,
    isCreateRoute,
    isStepsRoute,
  } = useBulkSendRoutes();

  const {
    createAction,
    updateProgress,
    updateAction,
  } = useActions({
    createAction: 'bulkSend/createBulkSend',
    updateProgress: 'bulkSend/updateProgress',
    updateAction: action,
  });

  /**
   * @param {import('vue-router').RawLocation} route
   * @param {boolean} [saveProgress]
   */
  async function doUpdate(route, saveProgress = true) {
    if (action && update.value) {
      try {
        await updateAction({
          fingerprint: currentRoute.params.fingerprint,
          data: update.value,
        });
      } catch (error) {
        onError(error);

        return;
      }
    }

    if (saveProgress && progressUpdate.value) {
      updateProgress({
        fingerprint: currentRoute.params.fingerprint,
        data: progressUpdate.value,
      });
    }

    await router.push(route);
  }

  const onBack = computed(() => {
    if (!prevRoute.value) {
      return undefined;
    }

    return () => router.push(prevRoute.value);
  });

  let isBusyTimeout;
  const isDisabled = ref(false);
  const isBusyOnSave = ref(false);
  const isBusyOnNext = ref(false);

  function clearBusy() {
    isDisabled.value = false;
    isBusyOnSave.value = false;
    isBusyOnNext.value = false;
    clearTimeout(isBusyTimeout);
  }

  const onSave = computed(() => {
    if (!isStepsRoute.value) {
      return undefined;
    }

    if (!validOrTrue.value) {
      return false;
    }

    return async () => {
      try {
        isDisabled.value = true;
        isBusyTimeout = setTimeout(() => isBusyOnSave.value = true, BUSY_DELAY); // eslint-disable-line no-return-assign

        await doUpdate({ name: 'BulkSends' }, false);
      } finally {
        clearBusy();
      }
    };
  });

  const onNext = computed(() => {
    if (!validOrTrue.value) {
      return false;
    }

    return async () => {
      try {
        isDisabled.value = true;
        isBusyTimeout = setTimeout(() => isBusyOnNext.value = true, BUSY_DELAY); // eslint-disable-line no-return-assign

        if (isCreateRoute.value) {
          const bulkSend = await createAction(update.value);
          await router.push({
            ...nextRoute.value,
            params: { fingerprint: bulkSend.fingerprint },
          });
        } else {
          await doUpdate(nextRoute.value);
        }
      } finally {
        clearBusy();
      }
    };
  });

  return {
    onNext,
    onSave,
    isBusyOnSave: readonly(isBusyOnSave),
    onBack,
    isBusyOnNext: readonly(isBusyOnNext),
    isDisabled: readonly(isDisabled),
  };
}

export function useBulkSendStepsProgress() {
  /** @type { import('@/store/modules/bulkSend').Getters} */
  const { isSent } = useGetters({ isSent: 'bulkSend/isSent' });

  const {
    stepsRoutes,
    currentIndex,
    isEditRoute,
    isCreateRoute,
  } = useBulkSendRoutes();

  const step = computed(() => (isCreateRoute.value ? 1 : currentIndex.value + 1));
  const steps = computed(() => (isSent.value || isEditRoute.value ? 0 : stepsRoutes.length));

  return { steps, step };
}
